import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventRound, HitsterEvent } from '../types';
import { sortRoundSongs } from '../utils/roundTimeline';
import './PrintAnswers.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PrintAnswers: React.FC = () => {
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
          const selectedRound = data.rounds.find((item: EventRound) => item.id === roundId);
          setRound(selectedRound || null);
        }
      } catch (error) {
        console.error('Failed to load event for answers print:', error);
      }
      setLoading(false);
    };

    loadData();
  }, [eventId, roundId]);

  const timelineSongs = useMemo(() => {
    if (!round) {
      return [];
    }

    return sortRoundSongs(round.songs)
      .sort((a, b) => {
        if ((a.year || 0) !== (b.year || 0)) {
          return (a.year || 0) - (b.year || 0);
        }
        return (a.order || 0) - (b.order || 0);
      })
      .map((song, index) => ({
        ...song,
        timelinePosition: index + 1,
      }));
  }, [round]);

  const handleBack = () => {
    navigate(`/round-scoring/${eventId}/${roundId}`);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div className="print-answers loading">Laden...</div>;
  }

  if (!event || !round) {
    return <div className="print-answers error">Ronde niet gevonden</div>;
  }

  return (
    <div className="print-answers">
      <div className="print-answers-controls no-print">
        <button
          onClick={handleBack}
          className="print-answers-close-btn"
          title="Terug"
          aria-label="Terug"
        >
          ×
        </button>
        <h1>Antwoordenformulier - {round.name}</h1>
        <button onClick={handlePrint} className="print-answers-print-btn">
          🖨️ Printen
        </button>
      </div>

      <div className="print-answers-content">
        <div className="print-answers-header">
          <h2>{event.name}</h2>
          <p>{round.name} • {timelineSongs.length} nummers • tijdlijnvolgorde (oud → nieuw)</p>
        </div>

        {timelineSongs.length > 0 ? (
          <div className="answers-timeline">
            {timelineSongs.map((song) => (
              <div key={song.id} className="timeline-item">
                <div className="timeline-position">{song.timelinePosition}</div>
                <div className="timeline-content">
                  <div className="timeline-main">
                    <span className="timeline-title">{song.title || '-'}</span>
                    <span className="timeline-separator">•</span>
                    <span className="timeline-artist">{song.artist || '-'}</span>
                  </div>
                  <div className="timeline-meta">
                    <span className="timeline-year">{song.year || '-'}</span>
                    <span className="timeline-order">Kaart #{song.order || '-'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-message">Geen nummers gevonden in deze ronde</div>
        )}
      </div>
    </div>
  );
};

export default PrintAnswers;
